import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { SERVICES, WHATSAPP_URL } from "../lib/content";
export default function Servico() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (service?.seo) {
      document.title = service.seo.title;
      const m = document.querySelector('meta[name="description"]');
      if (m) m.setAttribute("content", service.seo.description);
    }
  }, [service]);

  if (!service) return <Navigate to="/servicos" replace />;

  const otherServices = SERVICES.filter((s) => s.slug !== slug);

  return (
    <>
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 bg-white" data-testid={`service-page-${slug}`}>
        <div className="sca-container">
          <Reveal>
            <nav className="text-[12px] tracking-[0.2em] uppercase text-sca-steel mb-10 flex items-center gap-2 flex-wrap">
              <Link to="/" className="hover:text-sca-red">Início</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/servicos" className="hover:text-sca-red">Serviços</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-sca-black">{service.title}</span>
            </nav>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <Reveal className="lg:col-span-8">
              <p className="font-mono text-sm tracking-[0.32em] text-sca-red font-bold mb-6">/ {service.number} — Serviço</p>
              <h1 className="sca-h1 max-w-[20ch]">{service.h1}</h1>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-4">
              <p className="sca-body">{service.intro}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Big image */}
      <section className="relative">
        <Reveal>
          <div className="relative h-[60vh] md:h-[78vh] overflow-hidden">
            <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          </div>
        </Reveal>
      </section>

      {/* Sections */}
      <section className="sca-section bg-white">
        <div className="sca-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {service.sections.map((sec, idx) => (
            <Reveal key={idx} delay={idx * 0.1} className={`lg:col-span-${idx === 0 ? "7" : "5"}`}>
              <p className="sca-overline mb-5">[ {idx === 0 ? "Atuação" : "Benefícios"} ]</p>
              <h2 className="sca-h3 mb-8">{sec.title}</h2>
              <ul
                className="space-y-0"
                data-testid={`servico-${slug}-list-${idx === 0 ? "atuacao" : "beneficios"}`}
              >
                {sec.items.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-4 py-3.5 border-b border-sca-line"
                  >
                    <Check className="w-5 h-5 text-sca-red flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="font-sans text-base md:text-lg text-sca-black font-medium">{it}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-sca-black text-white py-24 md:py-32">
        <div className="sca-container grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <p className="sca-overline text-white/60 mb-6">[ {service.title} ]</p>
            <h3 className="font-sans text-3xl md:text-5xl lg:text-6xl font-bold tracking-tightest leading-[1.05] max-w-3xl">
              Pronto para estruturar a <span className="sca-serif text-sca-red">sua obra</span>?
            </h3>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:flex md:justify-end gap-3">
            <a
              href={WHATSAPP_URL(`Olá, gostaria de ${service.cta.toLowerCase()} relacionada a ${service.title.toLowerCase()}.`)}
              target="_blank"
              rel="noreferrer"
              className="btn-sca-primary"
              data-testid={`service-cta-${slug}`}
            >
              {service.cta} <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Outros serviços */}
      <section className="sca-section bg-sca-mist">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">[ Outros serviços ]</p>
            <h2 className="sca-h2 mb-12">Explore o portfólio técnico completo da SCA.</h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-sca-line border-t border-l border-sca-line">
            {otherServices.map((s) => (
              <StaggerItem key={s.slug}>
                <Link
                  to={`/servicos/${s.slug}`}
                  data-testid={`other-service-${s.slug}`}
                  className="group block bg-white p-8 border-r border-b border-sca-line hover:bg-sca-black hover:text-white transition-colors duration-500 h-full"
                >
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">{s.number}</span>
                    <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:text-sca-red transition-all" />
                  </div>
                  <h3 className="font-sans text-xl md:text-2xl font-semibold leading-tight">{s.title}</h3>
                  <p className="text-sm text-sca-steel group-hover:text-white/65 mt-3 leading-relaxed">{s.short}</p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
